<template>
  <div>
    <!-- Dialog -->
<!-- Dialog -->
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ $t('users.routeDialog.title') }}</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <!-- Route Selection -->
              <v-col cols="12">
                <v-select
                  v-model="selectedRoute"
                  :items="routes"
                  :item-text="(item) => item.name"
                  :item-value="(item) => item.id"
                  :label="$t('users.routeDialog.selectRouteLabel')"
                  prepend-icon="mdi-map-marker-path"
                  :loading="loadingRoutes"
                  @change="onRouteChange"
                  clearable
                  required
                ></v-select>
              </v-col>

              <!-- Stop Selection -->
              <v-col cols="12">
                <v-select
                  v-model="selectedStop"
                  :items="stops"
                  :item-text="(item) => item.name"
                  :item-value="(item) => item.id"
                  :label="$t('users.routeDialog.selectStopLabel')"
                  prepend-icon="mdi-bus-stop"
                  :disabled="!selectedRoute"
                  :loading="loadingStops"
                  @change="onStopChange"
                  clearable
                  required
                ></v-select>
              </v-col>

              <!-- Pickup Time Selection -->
              <v-col cols="12">
                <v-select
                  v-model="selectedTime"
                  :items="stopTimes"
                  :item-text="(item) => item.id"
                  :item-value="(item) => item.id"
                  :label="$t('users.routeDialog.selectTimeLabel')"
                  prepend-icon="mdi-clock-outline"
                  :disabled="!selectedStop"
                  :loading="loadingTimes"
                  @change="onTimeChange"
                  clearable
                  required
                ></v-select>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="cancelSelection"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="confirmSelection"
            :disabled="!isSelectionComplete"
          >
            {{ $t('common.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  props: {
    display_dialog: {
        type: Boolean,
        default: false
    },
    student_id: {
        type: Number,
        required: true
    },
    pick_up: {
        type: Boolean,
        default: true
    },
    student_ids: {
        type: Array,
        default: () => []
    }
  },
  name: 'RouteSelectionDialog',
  data() {
    return {
      dialog: false,
      selectedRoute: null,
      selectedStop: null,
      selectedTime: null,
      loadingRoutes: false,
      loadingStops: false,
      loadingTimes: false,
      lastSelection: null,

      routes: [],
      stops: [],
      stopTimes: [],
      stopTimesDetails: [],
      selectedStopTimesDetails: {},
    }
  },

  computed: {
    isSelectionComplete() {
      return this.selectedRoute && this.selectedStop && this.selectedTime
    }
  },
  watch: {
    display_dialog(newVal) {
        this.dialog = newVal;
        if (newVal) {
          this.loadRoutes();
        }
    }
  },

  methods: {
    loadRoutes() {
      this.loadingRoutes = true;
      this.routes = [];
      axios
        .get(`/routes/pickup-dropoff/${this.pick_up ? 1 : 0}`)
        .then((response) => {
          this.routes = response.data;
        })
        .catch((error) => {
          this.$notify({
            title: this.$t("common.error"),
            text: this.$t("users.routeDialog.errorRetrievingRoutes"),
            type: 'error'
          });
          console.log(error);
          this.$swal(this.$t("common.error"), error.response.data.message, "error");
        })
        .then(() => {
          this.loadingRoutes = false;
        });
    },
    loadStops(route_id) {
      this.loadingStops = true;
      this.stops = [];
      axios
        .get(`/routes/${route_id}/stops`)
        .then((response) => {
          this.stops = response.data.stops || [];
        })
        .catch((error) => {
          this.$notify({
            title: this.$t("common.error"),
            text: this.$t("users.routeDialog.errorRetrievingStops"),
            type: "error",
          });
          console.log(error);
          this.$swal(this.$t("common.error"), error.response.data.message, "error");
        })
        .then(() => {
          this.loadingStops = false;
        });
    },
    loadTimes(route_id, stop_id, student_id, pick_up)
    {
        this.loadingTimes = true;
        axios
            .get(`/stops/get-stop-times/all`, {
                params: {
                    route_id: route_id,
                    stop_id: stop_id,
                    student_id: student_id,
                    pick_up: pick_up
                }
            })
            .then((response) => {
                this.stopTimesDetails = response.data.times;
                this.stopTimes = pick_up == 1 ? response.data.times.pick_times : response.data.times.drop_times;
            })
            .catch((error) => {
                this.$notify({
                    title: this.$t("common.error"),
                    text: this.$t("users.routeDialog.errorRetrievingTimes"),
                    type: "error",
                });
                console.log(error);
                this.$swal(this.$t("common.error"), error.response.data.message, "error");
            })
            .finally(() => {
                this.loadingTimes = false;
            });
    },
    onRouteChange() {
      let routeId = this.selectedRoute;
      // Reset dependent selections
      this.selectedStop = null
      this.selectedTime = null
      this.stops = []
      this.stopTimes = []

      if (!routeId) return

      //load stops for the selected route
      this.loadStops(routeId)
    },

    onStopChange() {
      let stopId = this.selectedStop;
      // Reset dependent selections
      this.selectedTime = null
      this.stopTimes = []

      if (!stopId) return

      let studentId = this.student_id;
      if (studentId === -1)
      {
        studentId = this.student_ids[0]
      }

      this.loadTimes(this.selectedRoute, stopId, studentId, this.pick_up ? 1 : 0)
    },

    onTimeChange() {
      let time = this.selectedTime;
      if (!time) return;

      // Handle time change logic here
      console.log("Selected time:", time);
       //get the selection index
       let selectedIndex = this.stopTimes.findIndex(t => t === time);
       console.log("Selected index:", selectedIndex);

       //display the selected time
       this.selectedStopTimesDetails = {
         tripId: this.stopTimesDetails.trips_ids[selectedIndex].toString(),
         routeStopId: this.stopTimesDetails.routes_of_stop[selectedIndex].id.toString()
       };
       console.log("Selected time details:", this.selectedStopTimesDetails);
    },

    confirmSelection() {
      if (!this.isSelectionComplete) return

      // Find the display names for the selected items
      const route = this.routes.find(r => r.id === this.selectedRoute)
      const stop = this.stops.find(s => s.id === this.selectedStop)
      const time = this.stopTimes.find(t => t.time === this.selectedTime)

      this.lastSelection = {
        route: route?.name || '',
        stop: stop?.name || '',
        time: time?.display || ''
      }

      // Emit the selection or handle it as needed
      this.$emit('selection-confirmed', {
        studentId: this.student_id,
        studentIds: this.student_ids,
        routeId: this.selectedRoute,
        stopId: this.selectedStop,
        pickup: this.pick_up ? 1 : 0,
        tripId: this.selectedStopTimesDetails.tripId
      })

      this.closeDialog()
    },
    cancelSelection()
    {
        this.$emit('selection-cancelled')
        this.closeDialog()
    },
    closeDialog() {
      this.dialog = false
      // Optionally reset selections when closing
      this.resetSelections()
    },

    resetSelections() {
        this.selectedRoute = null
        this.selectedStop = null
        this.selectedTime = null
        this.stops = []
        this.stopTimes = []
    }
  }
}
</script>

<style scoped>
/* Add any custom styles here if needed */
</style>
